import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../api';

const emptyForm = {
  client_name: '',
  google_ads_customer_id: '',
  meta_ad_account_id: '',
  meta_page_id: '',
  telegram_chat_id: '',
  report_email: '',
  active: true,
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getClients();
      setClients(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (client) => {
    setForm({ ...emptyForm, ...client });
    setEditingId(client.id);
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await api.updateClient(editingId, form);
      } else {
        await api.createClient(form);
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client? This cannot be undone.')) return;
    try {
      await api.deleteClient(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Clients</h1>
        <button
          onClick={openNew}
          className="bg-brand-purple text-white px-4 py-2 rounded-lg font-medium hover:bg-brand-purpleDark"
        >
          + Add Client
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 text-sm rounded-lg px-3 py-2 mb-4">{error}</div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow p-6 mb-6 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
            <input name="client_name" value={form.client_name} onChange={handleChange} required
              className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Google Ads Customer ID</label>
            <input name="google_ads_customer_id" value={form.google_ads_customer_id || ''} onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meta Ad Account ID</label>
            <input name="meta_ad_account_id" value={form.meta_ad_account_id || ''} onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="act_..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meta Page ID</label>
            <input name="meta_page_id" value={form.meta_page_id || ''} onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telegram Chat ID</label>
            <input name="telegram_chat_id" value={form.telegram_chat_id || ''} onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Email</label>
            <input type="email" name="report_email" value={form.report_email || ''} onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" name="active" checked={form.active} onChange={handleChange} id="active" />
            <label htmlFor="active" className="text-sm text-gray-700">Active</label>
          </div>
          <div className="col-span-2 flex gap-3">
            <button type="submit" className="bg-brand-purple text-white px-4 py-2 rounded-lg font-medium hover:bg-brand-purpleDark">
              {editingId ? 'Save Changes' : 'Create Client'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="text-gray-500 px-4 py-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Total Ads</th>
                <th className="px-4 py-3">Live — Meta</th>
                <th className="px-4 py-3">Live — Google</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{c.client_name}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Google: {c.google_ads_customer_id || '—'} | Meta: {c.meta_ad_account_id || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 font-medium">{c.total_ads ?? 0}</td>
                  <td className="px-4 py-3 text-green-700 font-medium">{c.live_meta ?? 0}</td>
                  <td className="px-4 py-3 text-green-700 font-medium">{c.live_google ?? 0}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {c.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(c)} className="text-brand-purple hover:underline mr-3">Edit</button>
                    <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-400">No clients yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
